

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import GridView from "./GridView";
import SearchFilter from "./home/SearchFilter";
import NotificationBar from "./home/NotifictionBar";
import ListView from "./ListView";
import { useLocation } from "react-router-dom";
import { getPaginatedProducts } from "../services/vehicleServices.ts";
import VehiclesPageInput from "./common/VehiclesPageInput.jsx";
import VehiclesSortSelect from "./common/VehiclesSortSelect.jsx";
import SideBar from "./SideBar.jsx";
import api from "../lib/api.ts";
import Pagination from "./common/Pagination.jsx";

const VehiclesPage = ({ auctionId, laneId }) => {

  // --- State and logic (same as your code) ---
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [selectedLanes, setSelectedLanes] = useState([]);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [currentFilters, setCurrentFilters] = useState({});
  const [filters, setFilters] = useState({});
  const [expandedFilters, setExpandedFilters] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const initialMake = params.get("makeId") || "";
  const initialModel = params.get("modelId") || "";
  const initialYear = params.get("year") || "";
  const initialSearchTerm = params.get("search") || "";
  const hasInitialQuery = Boolean(
    initialMake || initialModel || initialYear || initialSearchTerm
  );
  const [vehicles, setVehicles] = useState([]);
  const [pageSize, setPageSize] = useState(12);
  const [sortBy, setSortBy] = useState("latest");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [laneData, setLaneData] = useState({});
  const [auctions, setAuctions] = useState([]);
  const [activeLane, setActiveLane] = useState("");
  const [selectedAuctionId, setSelectedAuctionId] = useState("");
  const [allAuctionsData, setAllAuctionsData] = useState({});
  const [totalVehiclesCount, setTotalVehiclesCount] = useState([0]);
  const [cardIndex, setCardIndex] = useState(0);
  const [auctionName, setAuctionName] = useState("Loading auctions...");
  const [showAllLanes, setShowAllLanes] = useState(true);
  const [searchResults, setSearchResults] = useState(null);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [selectedMake, setSelectedMake] = React.useState(initialMake);
  const [selectedModel, setSelectedModel] = React.useState(initialModel);
  const [selectedYear, setSelectedYear] = React.useState(initialYear);
  const latestRequestRef = useRef(0);

  // Set auction and lane from URL params when component mounts
  useEffect(() => {
    if (auctionId && laneId) {
      setSelectedAuctionId(auctionId);
      setActiveLane(laneId);

      // Fetch vehicles for specific auction and lane
      fetchVehiclesForAuctionAndLane();
    }
  }, [auctionId, laneId]);

  // Function to fetch vehicles for specific auction and lane
  const fetchVehiclesForAuctionAndLane = async () => {
    if (!auctionId || !laneId) return;

    try {

      // You can modify this API call to include auction and lane filters
      const res = await getPaginatedProducts({
        page: 1,
        limit: pageSize,
        order: "ASC",
        filters: {
          auctionId: auctionId,
          laneId: laneId,
          // Add other filters as needed
        },
      });

      setVehicles(res?.data || []);
      setTotalPages(res?.pages || 1);
      setCurrentPage(1);
      setTotalVehiclesCount(res?.total || res?.data?.length || 0);

    } catch (error) {
      console.error("Failed to fetch vehicles for auction and lane:", error);
    }
  };

  // Helper functions (same as your code)
  const safeGet = (obj, path, defaultValue = "") => {
    return path.split(".").reduce((current, key) => {
      return current && current[key] !== undefined
        ? current[key]
        : defaultValue;
    }, obj);
  };
  const areFiltersEqual = (filters1, filters2) => {
    const keys1 = Object.keys(filters1);
    const keys2 = Object.keys(filters2);
    if (keys1.length !== keys2.length) {
      return false;
    }
    for (let key of keys1) {
      if (filters1[key] !== filters2[key]) {
        return false;
      }
    }
    return true;
  };
  const getNumericValue = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const num = typeof value === "string" ? parseFloat(value) : Number(value);
    return isNaN(num) ? 0 : num;
  };
  const getDateValue = (vehicle) => {
    if (vehicle.startDate) {
      return new Date(vehicle.startDate).getTime();
    }
    return vehicle.id || 0;
  };

  // Sorting and display logic (same as your code)
  const sortedVehicles = useMemo(() => {
    return [...vehicles].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case "make":
          const makeA = safeGet(
            a,
            "MakeModel.make.name",
            safeGet(a, "make", "-")
          ).toLowerCase();
          const makeB = safeGet(
            b,
            "MakeModel.make.name",
            safeGet(b, "make", "-")
          ).toLowerCase();
          comparison = makeA.localeCompare(makeB);
          break;
        case "model":
          const modelA = safeGet(
            a,
            "MakeModel.name",
            safeGet(a, "model", "-")
          ).toLowerCase();
          const modelB = safeGet(
            b,
            "MakeModel.name",
            safeGet(b, "model", "-")
          ).toLowerCase();
          comparison = modelA.localeCompare(modelB);
          break;
        case "year":
          const yearA = getNumericValue(a.year);
          const yearB = getNumericValue(b.year);
          comparison = yearB - yearA;
          break;
        case "mileage":
          const mileageA = getNumericValue(a.mileage);
          const mileageB = getNumericValue(b.mileage);
          comparison = mileageA - mileageB;
          break;
        case "price":
          const priceA = getNumericValue(
            a.sellingPrice || a.price || a.currentBid || a.startingBid || 0
          );
          const priceB = getNumericValue(
            b.sellingPrice || b.price || b.currentBid || b.startingBid || 0
          );
          comparison = priceA - priceB;
          break;
        case "latest":
        default:
          const dateA = getDateValue(a);
          const dateB = getDateValue(b);
          comparison = dateB - dateA;
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [vehicles, sortBy, sortDirection]);

  const getDisplayData = () => {
    if (isSearchActive && searchResults) {
      return searchResults;
    }
    let dataToSort = [];
    if (!selectedAuctionId) {
      dataToSort = sortedVehicles;
    } else if (showAllLanes && selectedAuctionId) {
      dataToSort = [];
      Object.values(laneData).forEach((lane) => {
        dataToSort.push(...lane);
      });
    } else if (selectedAuctionId && activeLane && laneData[activeLane]) {
      dataToSort = [...laneData[activeLane]];
    } else {
      return [];
    }
    if (selectedAuctionId && dataToSort.length > 0) {
      return dataToSort.sort((a, b) => {
        let comparison = 0;
        switch (sortBy) {
          case "make":
            const makeA = safeGet(
              a,
              "MakeModel.make.name",
              safeGet(a, "make", "-")
            ).toLowerCase();
            const makeB = safeGet(
              b,
              "MakeModel.make.name",
              safeGet(b, "make", "-")
            ).toLowerCase();
            comparison = makeA.localeCompare(makeB);
            break;
          case "model":
            const modelA = safeGet(
              a,
              "MakeModel.name",
              safeGet(a, "model", "-")
            ).toLowerCase();
            const modelB = safeGet(
              b,
              "MakeModel.name",
              safeGet(b, "model", "-")
            ).toLowerCase();
            comparison = modelA.localeCompare(modelB);
            break;
          case "year":
            const yearA = getNumericValue(a.year);
            const yearB = getNumericValue(b.year);
            comparison = yearB - yearA;
            break;
          case "mileage":
            const mileageA = getNumericValue(a.mileage);
            const mileageB = getNumericValue(b.mileage);
            comparison = mileageA - mileageB;
            break;
          case "price":
            const priceA = getNumericValue(
              a.sellingPrice || a.price || a.currentBid || a.startingBid || 0
            );
            const priceB = getNumericValue(
              b.sellingPrice || b.price || b.currentBid || b.startingBid || 0
            );
            comparison = priceA - priceB;
            break;
          case "latest":
          default:
            const dateA = getDateValue(a);
            const dateB = getDateValue(b);
            comparison = dateB - dateA;
            break;
        }
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }
    return dataToSort;
  };

  const displayData = getDisplayData();

  const fetchVehicles = async (params = {}) => {
    try {
      const requestId = ++latestRequestRef.current;
      if (
        topSearchFiltersParms &&
        Object.keys(topSearchFiltersParms).length > 0
      ) {
        params = { ...params, ...topSearchFiltersParms };
      }

      if (sideBarParams && Object.keys(sideBarParams).length > 0) {
        params = { ...params, ...sideBarParams };
      }

      const res = await getPaginatedProducts({
        page: currentPage,
        limit: pageSize,
        order: "ASC" | "DESC",
        search: params.search,
        filters: {
          ...params,
          // search: searchTerm,
          // makeName: selectedMake || undefined,
          // MakeModelName: selectedModel || undefined,
          // year: selectedYear || undefined,
          // BodyType: 45,
          // DriveLineType: 42,
          // TransmissionType: 22,
          // fuelType: 34,
          // cylinder: 51,
          // engine: 39,
          // Highlight: 20,

          // primaryDamageType: 4,
          // secondaryDamageType: 3,

          // variant: 61,
          // Add any other filters you need

          // Add if needed
        },
      });
      // Ignore stale responses
      if (requestId !== latestRequestRef.current) return;
      setVehicles(res?.data || []);
      setTotalPages(res?.pages || 1);
      setCurrentPage(res?.page || 1);

      // Always update total count (also in search mode)
      setTotalVehiclesCount(res?.total || res?.data?.length || 0);
    } catch (error) {
      console.error("Failed to fetch vehicles:", error);
    }
  };

  // Fetch Vehicles (Main API) - skip initial fetch if URL contains initial query until search is active
  useEffect(() => {
    if (hasInitialQuery && !isSearchActive) return;
    fetchVehicles();
  }, [currentPage, pageSize, isSearchActive, hasInitialQuery]);

  const [topSearchFiltersParms, setTopSearchFiltersParams] = useState({});

  // Function to handle search results from SearchFilter component
  const handleSearchResults = (
    selectedMake,
    selectedModel,
    selectedYear,
    searchTerm
  ) => {
    const paramsObj = {
      makeName: selectedMake,
      MakeModelName: selectedModel,
      year: selectedYear,
      search: searchTerm,
    };
    setTopSearchFiltersParams(paramsObj);
    setSearchParams(paramsObj);
    setIsSearchActive(true);
    setCurrentPage(1);
    // Trigger fetch immediately with these params
    fetchVehicles(paramsObj);
  };

  // Fetch Auctions
  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        const res = await api.get(
          `/auctions/paginated?type=upcomming&orderBy=startDate&order=ASC&filters[isActive]=true`
        );
        const fetchedAuctions = res.data?.data || [];
        setAuctions(fetchedAuctions);

        const allData = {};
        let totalAuctionVehicles = 0;

        fetchedAuctions.forEach((auction) => {
          const lanes = {};
          let auctionVehicleCount = 0;

          const sortedLanes = (auction.auctionLanes || []).sort((a, b) => {
            const laneA = a.lane?.name || `Lane ${a.laneId}`;
            const laneB = b.lane?.name || `Lane ${b.laneId}`;
            return laneA.localeCompare(laneB, undefined, {
              numeric: true,
              sensitivity: "base",
            });
          });

          sortedLanes.forEach((laneObj) => {
            const laneName = laneObj.lane?.name || `Lane ${laneObj.laneId}`;
            const vehicles = (laneObj.auctionLaneVehicles || []).map(
              (laneVehicle) => {
                const vehicle = laneVehicle.vehicle || {};
                return {
                  ...laneVehicle,
                  ...vehicle,
                  id:
                    vehicle.id ||
                    laneVehicle.vehicleId ||
                    Math.random().toString(36).substr(2, 9),
                  title:
                    vehicle.name ||
                    `${vehicle.make || vehicle.MakeModel?.make?.name || ""} ${
                      vehicle.model || vehicle.MakeModel?.name || ""
                    }`.trim(),
                  image: vehicle.images?.[0]?.url || "/default-car.jpg",
                  startDate: auction.startDate,
                  isAuctionItem: true,
                  MakeModel: vehicle.MakeModel || {
                    make: { name: vehicle.make || "-" },
                    name: vehicle.model || "-",
                  },
                  year: vehicle.year || "-",
                  mileage: vehicle.mileage || 0,
                  // sellingPrice: vehicle.sellingPrice || laneVehicle.price || 0,
                  currentBid: vehicle.currentBid || laneVehicle.currentBid || 0,
                  auctionName: auction.name,
                  laneName: laneName,
                  BodyType: vehicle.BodyType,
                  DriveLineType: vehicle.DriveLineType,
                  ExteriorColor: vehicle.ExteriorColor,
                  InteriorColor: vehicle.InteriorColor,
                  TransmissionType: vehicle.TransmissionType,
                  engine: vehicle.engine,
                  fuelType: vehicle.fuelType,
                  cylinder: vehicle.cylinder,
                  primaryDamageType: vehicle.primaryDamageType,
                  secondaryDamageType: vehicle.secondaryDamageType,
                  lotNumber: vehicle.lotNumber,
                  startingBid: vehicle.startingBid,
                  bidIncrement: vehicle.bidIncrement,
                  reservedPrice: vehicle.reservedPrice,
                  ProductStatus: vehicle.ProductStatus,
                  Highlight: vehicle.Highlight,
                  variant: vehicle.variant,
                  images: vehicle.images || [],
                };
              }
            );

            lanes[laneName] = vehicles;
            auctionVehicleCount += vehicles.length;
            totalAuctionVehicles += vehicles.length;
          });

          const sortedNames = Object.keys(lanes).sort((a, b) =>
            a.localeCompare(b, undefined, {
              numeric: true,
              sensitivity: "base",
            })
          );

          allData[auction.id] = {
            lanes,
            name: auction.name,
            totalVehicles: auctionVehicleCount,
            sortedNames,
          };
        });

        setAllAuctionsData(allData);
        setSelectedAuctionId("");
        setLaneData({});
        setActiveLane("");
        setAuctionName("Select an auction");
      } catch (err) {
        console.error("Error fetching auctions:", err);
        setLaneData({});
        setActiveLane("");
        setSelectedAuctionId("");
        setAllAuctionsData({});
        setAuctionName("No auctions available");
      }
    };

    fetchAuctions();
  }, []);

  // const toggleAllLanes = () => {
  //   setShowAllLanes(!showAllLanes);
  //   if (!showAllLanes) {
  //     setActiveLane("All Lanes");
  //   } else if (
  //     selectedAuctionId &&
  //     allAuctionsData[selectedAuctionId]?.sortedNames?.length
  //   ) {
  //     setActiveLane(allAuctionsData[selectedAuctionId].sortedNames[0]);
  //   }
  // };
  const toggleAllLanes = () => {
    setShowAllLanes(!showAllLanes);
    setActiveLane("All Lanes");
    setTotalVehiclesCount(
      allAuctionsData[selectedAuctionId]?.totalVehicles || 0
    );
  };
  const handleAuctionChange = (event) => {
    const auctionId = event.target.value;
    setSelectedAuctionId(auctionId);
    setCardIndex(0);
    setShowAllLanes(true);

    // Clear search results when switching auctions
    clearSearchResults();

    if (auctionId && allAuctionsData[auctionId]) {
      const selectedAuctionData = allAuctionsData[auctionId];
      setLaneData(selectedAuctionData.lanes);
      setActiveLane("All Lanes");
      setAuctionName(
        `${selectedAuctionData.name} (${selectedAuctionData.totalVehicles})`
      );
      setTotalVehiclesCount(selectedAuctionData.totalVehicles);
    } else {
      setLaneData({});
      setActiveLane("");
      setAuctionName("All Vehicles");
      setTotalVehiclesCount(vehicles.length);
    }
  };
  // ----------------------SearchAnd Filter----------------------

  // Clear search results function
  const clearSearchResults = () => {
    setSearchResults(null);
    setIsSearchActive(false);
    setSearchParams({}); // <-- reset search params
    setTotalVehiclesCount(vehicles.length);

    // Re-apply just filters (no search)
    fetchVehicles(currentFilters);
  };

  // ========================================
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // (Removed duplicate auto-fetch effect to avoid overriding search results)

  // --- When sidebar filter changes
  const [sideBarParams, setSideBarParams] = useState({});
  const handleSidebarFilterChange = useCallback(
    (params) => {
      const paramsFormated = Object.fromEntries(
        Object.entries(params).map(([key, value]) => [
          key,
          Array.isArray(value) ? value.join(",") : value,
        ])
      );
      if (!areFiltersEqual(currentFilters, paramsFormated)) {
        setCurrentFilters(paramsFormated);
        // Always merge sidebar + search params
        const finalParams = { ...paramsFormated, ...searchParams };
        setSideBarParams(finalParams);
        setCurrentPage(1);
        // fetchVehicles();
      }
    },
    [currentFilters, searchParams]
  );

  // Add this useEffect to watch for changes
  useEffect(() => {
    if (Object.keys(topSearchFiltersParms).length > 0) {
      fetchVehicles();
    }
    if (Object.keys(sideBarParams).length > 0) {
      fetchVehicles();
    }
  }, [topSearchFiltersParms, sideBarParams]);

  const handleSetCurrentPage = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (initialMake || initialModel || initialYear || initialSearchTerm) {
      handleSearchResults(
        initialMake,
        initialModel,
        initialYear,
        initialSearchTerm
      );
    }
    // eslint-disable-next-line
  }, [initialMake, initialModel, initialYear, initialSearchTerm]);

  // --- Fetch vehicles, auctions, handle filters, etc. (same as your code) ---
  // ... (all your fetch logic and handlers here) ...

  // --- Sidebar toggle ---
  // const toggleSidebar = () => {
  //   setIsSidebarOpen(!isSidebarOpen);
  // };

  // --- Render ---
  return (
    <>
      <NotificationBar />
      {/* Hero Section - always full width */}
      <div
        className=" max-w-7xl mx-auto  w-full min-h-[200px] bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url('https://static.vecteezy.com/system/resources/previews/059/360/470/non_2x/the-mercedes-s-class-is-driving-on-a-city-street-at-night-photo.jpeg')",
        }}
      >
        <div className="absolute top-0 left-0 w-full h-full bg-[#000000a2] z-10" />
        <div className="relative z-20 px-5 md:px-[60px] ">
          {/* <h2 className='tex-[40px] md:text-[40px] font-600 mb-8'>Vehicles</h2> */}
          <div className="pt-20">
            <SearchFilter
              isRTL={false}
              language="en"
              shadow="none"
              initialMake={initialMake}
              initialModel={initialModel}
              isHomepage={false}
              initialYear={initialYear}
              initialSearchTerm={initialSearchTerm}
              containerBg="transparent"
              inputBg="transparent"
              fetchVehicles={fetchVehicles}
              onSearchResults={handleSearchResults}
              onClearSearch={clearSearchResults}
            />
          </div>
          {/* Toggle Sidebar Button */}
          <div>
            <button
              onClick={toggleSidebar}
              className={` bg-[#F15A29] hover:bg-[#ff5f2a] text-white px-3 py-2 rounded-r-md transition-transform duration-300 flex items-center gap-2`}
            >
              <span>Filters</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 34 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {isSidebarOpen ? (
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M21.6004 27.1157C22.0215 26.7583 22.0732 26.1273 21.7159 25.7062L14.3171 16.9866L21.8025 8.3413C22.164 7.92377 22.1186 7.29223 21.7011 6.93073C21.2835 6.56922 20.652 6.61463 20.2905 7.03216L12.2441 16.3255C11.9212 16.6984 11.9184 17.251 12.2376 17.627L20.1909 27.0002C20.5482 27.4213 21.1793 27.473 21.6004 27.1157Z"
                    fill="white"
                  />
                ) : (
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.3996 27.1157C11.9785 26.7583 11.9268 26.1273 12.2841 25.7062L19.6829 16.9866L12.1975 8.3413C11.836 7.92377 11.8814 7.29223 12.2989 6.93073C12.7165 6.56922 13.348 6.61463 13.7095 7.03216L21.7559 16.3255C22.0788 16.6984 22.0816 17.251 21.7624 17.627L13.8091 27.0002C13.4518 27.4213 12.8207 27.473 12.3996 27.1157Z"
                    fill="white"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Browse Results + Sidebar */}
      <div className="max-w-7xl mx-auto flex min-h-screen font-poppins bg-[#FAFAFA]">
        {/* Sidebar */}
        <div
          style={{
            width: isSidebarOpen ? 260 : 0,
            transition: "width 0.3s",
            overflow: "hidden",
            background: "#414042",
          }}
        >
          <SideBar onCheckBoxChange={handleSidebarFilterChange} />
        </div>

        {/* Main Content */}
        <div className="flex-1 transition-all duration-300">
          {/* Search Results Section */}
          <div className="bg-white font-sans min-h-screen p-6 text-black">
            {/* Header */}
            <div className="mb-4">
              <h2 className="text-[28px] font-[600] mb-2">
                {isSearchActive ? "Search" : "Browse"}{" "}
                <span className="text-[#F15A29]">Vehicles </span>
                <span className="text-primary-dark">
                  ({totalVehiclesCount})
                </span>
              </h2>

              {/* Auction and Lane Information */}
              {auctionId && laneId && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-semibold">
                        Auction ID:
                      </span>
                      <span className="text-gray-800">{auctionId}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-semibold">
                        Lane ID:
                      </span>
                      <span className="text-gray-800">{laneId}</span>
                    </div>
                  </div>
                  <p className="text-sm text-blue-600 mt-2">
                    Showing vehicles for specific auction and lane
                  </p>
                </div>
              )}
              {isSearchActive && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-gray-600">
                    Showing search results
                  </span>
                  <button
                    onClick={clearSearchResults}
                    className="text-[#F15A29] text-sm hover:underline"
                  >
                    Clear Search
                  </button>
                </div>
              )}
              <hr className="border-gray-300" />
            </div>

            {/* Filters - Hide when in search mode */}
            {!isSearchActive && (
              <div className="flex flex-col md:flex-row items-center justify-between w-full gap-4 mb-4">
                {/* Left Filters: Auction & Lanes */}
                <div className="flex flex-col md:flex-row items-center gap-4">
                  {/* Auction Select */}
                  <div className="flex flex-col mb-2 md:mb-0">
                    <select
                      value={selectedAuctionId}
                      onChange={handleAuctionChange}
                      className="border rounded-[10px] px-3 py-2 text-sm"
                    >
                      <option value="">All Auctions</option>
                      {auctions.map((auction) => (
                        <option key={auction.id} value={auction.id}>
                          {auction.name} (
                          {allAuctionsData[auction.id]?.totalVehicles || 0})
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* {selectedAuctionId && Object.keys(laneData).length > 0 && (
                    <div className='flex gap-3 flex-wrap'>
                      <button
                        onClick={toggleAllLanes}
                        className={`px-3 py-1 rounded-md font-medium transition-colors duration-300 ${
                          showAllLanes
                            ? "bg-[#F15A29] text-white"
                            : "bg-[#201111] text-white hover:bg-primary-dark"
                        }`}
                      >
                        All Lanes
                      </button>
                      {Object.keys(laneData).map((lane) => (
                        <button
                          key={lane}
                          onClick={() => {
                            setActiveLane(lane);
                            setShowAllLanes(false);
                          }}
                          className={`px-3 py-1 rounded-md font-medium transition-colors duration-300 ${
                            !showAllLanes && lane === activeLane
                              ? "bg-[#F15A29] text-white"
                              : "bg-[#201111] text-white hover:bg-primary-dark"
                          }`}
                        >
                          {lane} ({laneData[lane]?.length || 0})
                        </button>
                      ))}
                    </div>
                  )} */}
                  {selectedAuctionId && Object.keys(laneData).length > 0 && (
                    <div className="flex gap-3 flex-wrap">
                      <button
                        onClick={toggleAllLanes}
                        className={`px-3 py-1 rounded-md font-medium transition-colors duration-300 ${
                          showAllLanes
                            ? "bg-[#F15A29] text-white"
                            : "bg-[#201111] text-white hover:bg-primary-dark"
                        }`}
                      >
                        All Lanes (
                        {allAuctionsData[selectedAuctionId]?.totalVehicles || 0}
                        )
                      </button>
                      {Object.keys(laneData).map((lane) => (
                        <button
                          key={lane}
                          onClick={() => {
                            setActiveLane(lane);
                            setShowAllLanes(false);
                            setTotalVehiclesCount(laneData[lane]?.length || 0); // <-- Set lane count here
                          }}
                          className={`px-3 py-1 rounded-md font-medium transition-colors duration-300 ${
                            !showAllLanes && lane === activeLane
                              ? "bg-[#F15A29] text-white"
                              : "bg-[#201111] text-white hover:bg-primary-dark"
                          }`}
                        >
                          {lane} ({laneData[lane]?.length || 0})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Right Controls: View Toggle, Page Size, Sort */}
                <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
                  {/* View Toggle Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewMode("list")}
                      className={`h-10 px-2 rounded-[8px] transition-colors duration-300 ${
                        viewMode === "list"
                          ? "bg-darkGraybg text-white"
                          : "bg-transparent hover:bg-darkGraybg hover:text-white"
                      }`}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill={viewMode === "list" ? "white" : "#000000"}
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M8 21C8.55 21 9.02083 20.8042 9.4125 20.4125C9.80417 20.0208 10 19.55 10 19V15C10 14.45 9.80417 13.9792 9.4125 13.5875C9.02083 13.1958 8.55 13 8 13H4C3.45 13 2.97917 13.1958 2.5875 13.5875C2.19583 13.9792 2 14.45 2 15V19C2 19.55 2.19583 20.0208 2.5875 20.4125C2.97917 20.8042 3.45 21 4 21H8ZM8 19H4V15H8V19ZM22 18V16H13V18H22ZM8 11C8.55 11 9.02083 10.8042 9.4125 10.4125C9.80417 20.0208 10 9.55 10 9V5C10 4.45 9.80417 3.97917 9.4125 3.5875C9.02083 3.19583 8.55 3 8 3H4C3.45 3 2.97917 3.19583 2.5875 3.5875C2.19583 3.97917 2 4.45 2 5V9C2 9.55 2.19583 10.0208 2.5875 10.4125C2.97917 10.8042 3.45 11 4 11H8ZM8 9H4V5H8V9ZM22 8V6H13V8H22Z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode("card")}
                      className={`h-10 px-2 rounded-[8px] transition-colors duration-300 ${
                        viewMode === "card"
                          ? "bg-darkGraybg text-white"
                          : "bg-transparent hover:bg-darkGraybg hover:text-white"
                      }`}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill={viewMode === "card" ? "white" : "#000000"}
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M3 11V3H11V11H3ZM3 21V13H11V21H3ZM13 11V3H21V11H13ZM13 21V13H21V21H13ZM5 9H9V5H5V9ZM15 9H19V5H15V9ZM15 19H19V15H15V19ZM5 19H9V15H5V19Z" />
                      </svg>
                    </button>
                  </div>
                  <VehiclesPageInput
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                  />
                  <VehiclesSortSelect
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    sortDirection={sortDirection}
                    setSortDirection={setSortDirection}
                  />
                </div>
              </div>
            )}
            {/* Show view controls for search mode */}
            {isSearchActive && (
              <div className="flex flex-col md:flex-row items-center justify-end w-full gap-4 mb-4">
                {/* View Toggle Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode("list")}
                    className={`h-10 px-2 rounded-[8px] transition-colors duration-300 ${
                      viewMode === "list"
                        ? "bg-darkGraybg text-white"
                        : "bg-transparent hover:bg-darkGraybg hover:text-white"
                    }`}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill={viewMode === "list" ? "white" : "#000000"}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M8 21C8.55 21 9.02083 20.8042 9.4125 20.4125C9.80417 20.0208 10 19.55 10 19V15C10 14.45 9.80417 13.9792 9.4125 13.5875C9.02083 13.1958 8.55 13 8 13H4C3.45 13 2.97917 13.1958 2.5875 13.5875C2.19583 13.9792 2 14.45 2 15V19C2 19.55 2.19583 20.0208 2.5875 20.4125C2.97917 20.8042 3.45 21 4 21H8ZM8 19H4V15H8V19ZM22 18V16H13V18H22ZM8 11C8.55 11 9.02083 10.8042 9.4125 10.4125C9.80417 20.0208 10 9.55 10 9V5C10 4.45 9.80417 3.97917 9.4125 3.5875C9.02083 3.19583 8.55 3 8 3H4C3.45 3 2.97917 3.19583 2.5875 3.5875C2.19583 3.97917 2 4.45 2 5V9C2 9.55 2.19583 10.0208 2.5875 10.4125C2.97917 10.8042 3.45 11 4 11H8ZM8 9H4V5H8V9ZM22 8V6H13V8H22Z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode("card")}
                    className={`h-10 px-2 rounded-[8px] transition-colors duration-300 ${
                      viewMode === "card"
                        ? "bg-darkGraybg text-white"
                        : "bg-transparent hover:bg-darkGraybg hover:text-white"
                    }`}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill={viewMode === "card" ? "white" : "#000000"}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M3 11V3H11V11H3ZM3 21V13H11V21H3ZM13 11V3H21V11H13ZM13 21V13H21V21H13ZM5 9H9V5H5V9ZM15 9H19V5H15V9ZM15 19H19V15H15V19ZM5 19H9V15H5V19Z" />
                    </svg>
                  </button>
                </div>
                <VehiclesPageInput
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                />
                <VehiclesSortSelect
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  sortDirection={sortDirection}
                  setSortDirection={setSortDirection}
                />
              </div>
            )}
            {/* Conditional Table or Card View */}
            {viewMode === "list" ? (
              <ListView
                listViewData={displayData}
                pageSize={pageSize}
                searchResults={isSearchActive ? searchResults : null}
              />
            ) : (
              <GridView
                gridViewData={displayData}
                pageSize={pageSize}
                isAuctionView={!!selectedAuctionId}
                searchResults={isSearchActive ? searchResults : null}
                selectedAuctionId={selectedAuctionId}
                laneData={laneData}
                activeLane={activeLane}
              />
            )}
            <div>
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VehiclesPage;
